import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { getCatalogs } from '../firestore/Catalog'
import {
  getSettingsProcessingVideo,
  uploadSettingsProcessingVideo,
} from '../firestore/SettingsProcessingVideo'
import { createSlideDeck, getSlideDecks } from '../firestore/SlideDeck'
import { Catalog } from '../models/Catalog'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SettingsProcessingVideo } from '../models/SettingsProcessingVideo'
import { SlideDeck, SlideDeckState } from '../models/SlideDeck'
import { SlideDeckGroup } from '../stores/SlideDeckGroup'
import type { StaticModelCollection } from '../types'
import { Cubit } from './core'
import { SettingsSlideDeckTag } from '../models/SettingsSlideDeckTag'
import {
  getSettingsSlideDeckTags,
  hideSettingsSlideDeckTag,
  saveSettingsSlideDeckTags,
} from '../firestore/SettingsSlideDeckTag'
import { httpsCallable } from 'firebase/functions'
import { captureException } from '@sentry/core'
import { FirebaseError } from 'firebase/app'
import {
  addUserProfileFavoriteSlideDeck,
  getUserProfileFavoriteSlideDecks,
  removeUserProfileFavoriteSlideDeck,
} from '../firestore/UserProfileFavoriteSlideDeck'
import type { UserProfileFavoriteSlideDeck } from '../models/UserProfileFavoriteSlideDeck'
import { UserProfileFavoriteSlideDeck as UserProfileFavoriteSlideDeckModel } from '../models/UserProfileFavoriteSlideDeck'
import { SlideDeckType } from '../types'
import { chainedDependentOn, dependentOn } from '../util/dependentOn'
import {
  fetchFeedbackAggregateForSlideDecksById,
  fetchFeedbackAggregateForSlideDecksByTypeId,
} from '../firestore/SlideDeck'

export type ExperienceTab =
  | 'all'
  | 'published'
  | 'drafts'
  | 'favorites'
  | 'archived'
  | 'demos'
  | 'globalAssets'
export type ExperienceSortStrategy =
  | 'name'
  | 'lastUpdated'
  | 'experienceType'
  | 'catalogs'

export type FilteredSlideDeckGroup = {
  entireGroup: SlideDeckGroup
  filteredSlideDecks: SlideDeck[]
}

export class AdminLibraryCubit extends Cubit {
  repository: FirebaseRepository

  slideDecks: StaticModelCollection<SlideDeck>
  catalogs: StaticModelCollection<Catalog>
  @observable searchPills: string[] = []

  settingsProcessingVideo: SettingsProcessingVideo
  private _settingsSlideDeckTags: StaticModelCollection<SettingsSlideDeckTag>

  @observable selectedTab: ExperienceTab | undefined = undefined
  @observable sortingStrategy: ExperienceSortStrategy = 'lastUpdated'
  @observable sortingOrder: 'asc' | 'desc' = 'desc'

  @observable slideDeckTypeFilter: SlideDeckType | 'all' = 'all'

  favoriteSlideDecks: StaticModelCollection<UserProfileFavoriteSlideDeck>
  @observable favoritingSlideDeckIds = observable.set<string>()
  private initialFavoritesLoaded = false

  @observable feedbackAggregateByDeckId: Map<
    string,
    { totalScore: number; totalFeedback: number }
  > = new Map()

  @observable feedbackAggregateByTypeId: Map<
    string,
    {
      totalScore: number
      totalFeedback: number
      totalScoreRecent: number
      totalFeedbackRecent: number
    }
  > = new Map()

  @observable feedbackAggregatesLoaded = false

  constructor(
    repository: FirebaseRepository,
    options: { searchPills?: string[]; selectedTab?: ExperienceTab } = {}
  ) {
    super()
    makeObservable(this)

    if (options.searchPills) {
      this.searchPills = options.searchPills
    }

    if (options.selectedTab) {
      this.setSelectedTab(options.selectedTab)
    }

    this.repository = repository
    this.slideDecks = SlideDeck.emptyCollection(repository)
    this.catalogs = Catalog.emptyCollection(repository)
    this.settingsProcessingVideo = SettingsProcessingVideo.empty(repository)
    this._settingsSlideDeckTags =
      SettingsSlideDeckTag.emptyCollection(repository)
    this.favoriteSlideDecks =
      UserProfileFavoriteSlideDeckModel.emptyCollection(repository)
  }

  initialize(): void {
    if (!this.repository.currentUser) {
      return
    }
    this.addStream(
      getUserProfileFavoriteSlideDecks(this.repository, {
        userId: this.repository.currentUser.uid,
      }),
      (favoriteSlideDecks) => {
        runInAction(() => {
          if (!this.initialFavoritesLoaded) {
            if (this.selectedTab === undefined) {
              if (favoriteSlideDecks.length > 0) {
                this.selectedTab = 'favorites'
              } else {
                this.selectedTab = 'all'
              }
            }
            this.initialFavoritesLoaded = true
          }
          this.favoriteSlideDecks.replaceModels(favoriteSlideDecks)
        })
      }
    )
    this.addStream(getSlideDecks(this.repository), (rooms) => {
      this.slideDecks.replaceModels(rooms)
    })
    this.addStream(getCatalogs(this.repository), (catalogs) => {
      this.catalogs.replaceModels(catalogs)
    })
    this.addStream(
      getSettingsProcessingVideo(this.repository),
      (settingsProcessingVideo) => {
        this.settingsProcessingVideo.replaceModel(settingsProcessingVideo)
      }
    )
    Promise.all([
      fetchFeedbackAggregateForSlideDecksById(this.repository),
      fetchFeedbackAggregateForSlideDecksByTypeId(this.repository),
    ])
      .then(([byId, byTypeId]) => {
        runInAction(() => {
          this.feedbackAggregateByDeckId = new Map(Object.entries(byId))
          this.feedbackAggregateByTypeId = new Map(Object.entries(byTypeId))
          this.feedbackAggregatesLoaded = true
        })
      })
      .catch(() => {})
  }

  @action
  setSearchPills(pills: string[]) {
    this.searchPills = pills
  }

  @action
  setSlideDeckTypeFilter(filter: SlideDeckType | 'all') {
    this.slideDeckTypeFilter = filter
  }

  @action
  setSelectedTab(tab: ExperienceTab): void {
    this.selectedTab = tab
    if (tab === 'demos') {
      this.slideDeckTypeFilter = SlideDeckType.demo
    } else if (this.slideDeckTypeFilter === SlideDeckType.demo) {
      this.slideDeckTypeFilter = 'all'
    }
  }

  @action
  changeSortingStrategy = (
    strategy: ExperienceSortStrategy,
    order: 'asc' | 'desc'
  ) => {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  @computed
  get filteredSlideDeckGroupsBeforeTypeFilter() {
    return dependentOn(
      !this.allSlideDecksGrouped.isLoading && this.favoriteSlideDecks.isLoaded,
      () => {
        const groups = this.allSlideDecksGrouped.value!
        const filteredGroups: FilteredSlideDeckGroup[] = []

        groups.forEach((group) => {
          const isArchived = (deck: SlideDeck) => deck.isArchived

          let filteredSlideDecks: SlideDeck[] = []

          switch (this.selectedTab) {
            case 'archived':
              filteredSlideDecks = group.slideDecks.filter(isArchived)
              break
            case 'all':
              filteredSlideDecks = group.slideDecks.filter(
                (deck) => !isArchived(deck)
              )
              break
            case 'published':
              filteredSlideDecks = group.slideDecks.filter(
                (deck) => deck.slideDeckState === SlideDeckState.published
              )
              break
            case 'drafts': {
              filteredSlideDecks = group.slideDecks.filter(
                (deck) => deck.slideDeckState === SlideDeckState.draft
              )
              break
            }
            case 'favorites':
              filteredSlideDecks = group.slideDecks.filter((deck) =>
                this.favoriteSlideDecks.models.some((f) => f.id === deck.id)
              )
              break
            case 'demos':
              filteredSlideDecks = group.slideDecks.filter(
                (deck) => !isArchived(deck)
              )
              break
            case 'globalAssets':
              filteredSlideDecks = []
              break
            default:
              filteredSlideDecks = group.slideDecks
              break
          }

          if (this.searchPills.length > 0) {
            filteredSlideDecks = filteredSlideDecks.filter((deck) => {
              const name = deck.data.slideDeckName?.toLowerCase() || ''
              const teaser = deck.data.slideDeckTeaser?.toLowerCase() || ''
              return this.searchPills.some(
                (pill) =>
                  name.includes(pill.toLowerCase()) ||
                  teaser.includes(pill.toLowerCase())
              )
            })
          }

          if (filteredSlideDecks.length > 0) {
            filteredGroups.push({
              entireGroup: group,
              filteredSlideDecks,
            })
          }
        })

        const sortedGroups = filteredGroups.sort((a, b) => {
          let compare = 0
          switch (this.sortingStrategy) {
            case 'name':
              compare = a.entireGroup.name.localeCompare(b.entireGroup.name)
              break
            case 'lastUpdated':
              compare =
                a.entireGroup.updatedAt.getTime() -
                b.entireGroup.updatedAt.getTime()
              break
            case 'experienceType': {
              const getDisplayString = (group: SlideDeckGroup): string => {
                const uniqueTypes = Array.from(
                  new Set(group.slideDecks.map((d) => d.data.slideDeckType))
                ).filter((type) => type !== undefined)
                if (uniqueTypes.length === 0) return '-'
                if (uniqueTypes.length === 1) {
                  if (uniqueTypes[0] === SlideDeckType.original) {
                    return 'Breakout Original'
                  }
                  const typeKey = SlideDeckType[uniqueTypes[0]] as string
                  return typeKey
                }
                return 'Multiple'
              }
              const aDisplay = getDisplayString(a.entireGroup)
              const bDisplay = getDisplayString(b.entireGroup)
              compare = aDisplay.localeCompare(bDisplay)
              break
            }
            case 'catalogs': {
              const aCatalogs = new Set(
                a.entireGroup.slideDecks.flatMap(
                  (deck) => deck.data.catalogIds || []
                )
              ).size
              const bCatalogs = new Set(
                b.entireGroup.slideDecks.flatMap(
                  (deck) => deck.data.catalogIds || []
                )
              ).size
              compare = aCatalogs - bCatalogs
              break
            }
          }
          return this.sortingOrder === 'asc' ? compare : -compare
        })

        return sortedGroups
      }
    )
  }

  @computed
  get filteredSlideDeckGroups() {
    return chainedDependentOn(
      this.filteredSlideDeckGroupsBeforeTypeFilter,
      (groups) => {
        if (this.slideDeckTypeFilter === 'all') {
          return groups
        }
        const filteredGroups: FilteredSlideDeckGroup[] = []

        groups.forEach((group) => {
          const filteredSlideDecks = group.filteredSlideDecks.filter(
            (deck) => deck.data.slideDeckType === this.slideDeckTypeFilter
          )

          if (filteredSlideDecks.length > 0) {
            filteredGroups.push({
              entireGroup: group.entireGroup,
              filteredSlideDecks,
            })
          }
        })

        return filteredGroups
      }
    )
  }

  @computed
  get allSlideDecksGrouped() {
    return dependentOn(
      this.slideDecks.isLoaded &&
        this.slideDecks.models.every((deck) => deck.isLoaded),
      () => {
        const grouped = new Map<string, SlideDeckGroup>()

        this.slideDecks.models.forEach((slideDeck) => {
          const parentId = slideDeck.data.slideDeckTypeId || slideDeck.id
          let group = grouped.get(parentId)

          if (!group) {
            group = new SlideDeckGroup(parentId, true)
            grouped.set(parentId, group)
          }

          group.addSlideDeck(slideDeck)
        })

        return Array.from(grouped.values())
      }
    )
  }

  @computed
  get nonArchivedSlideDeckGroups() {
    return chainedDependentOn(this.allSlideDecksGrouped, (slideDecksGroups) => {
      const filteredGroups: FilteredSlideDeckGroup[] = []

      slideDecksGroups.forEach((group) => {
        const nonArchivedSlideDecks = group.slideDecks.filter(
          (deck) => !deck.isArchived
        )

        if (nonArchivedSlideDecks.length > 0) {
          filteredGroups.push({
            entireGroup: group,
            filteredSlideDecks: nonArchivedSlideDecks,
          })
        }
      })

      return filteredGroups
    })
  }

  @computed
  get publishedSlideDeckGroupsCount() {
    return chainedDependentOn(this.allSlideDecksGrouped, (slideDecksGroups) => {
      return slideDecksGroups.filter((group) =>
        group.slideDecks.some(
          (slideDeck: SlideDeck) =>
            slideDeck.slideDeckState === SlideDeckState.published
        )
      ).length
    })
  }

  @computed
  get draftSlideDeckGroupsCount() {
    return chainedDependentOn(this.allSlideDecksGrouped, (slideDecksGroups) => {
      return slideDecksGroups.filter((group) =>
        group.slideDecks.some(
          (slideDeck: SlideDeck) =>
            slideDeck.slideDeckState === SlideDeckState.draft
        )
      ).length
    })
  }

  @computed
  get demoSlideDeckGroupsCount() {
    return chainedDependentOn(this.allSlideDecksGrouped, (slideDecksGroups) => {
      return slideDecksGroups.filter((group) =>
        group.slideDecks.some(
          (deck) =>
            deck.data.slideDeckType === SlideDeckType.demo && !deck.isArchived
        )
      ).length
    })
  }

  @computed
  get favoritedSlideDeckGroupsCount() {
    return dependentOn(
      this.favoriteSlideDecks.isLoaded && this.slideDecks.isLoaded,
      () => {
        return this.allSlideDecksGrouped.value!.filter((group) =>
          group.slideDecks.some((deck) =>
            this.favoriteSlideDecks.models.some((f) => f.id === deck.id)
          )
        ).length
      }
    )
  }

  @computed
  get settingsSlideDeckTags() {
    const streamKey = 'settingsSlideDeckTags'
    if (!this.hasStream(streamKey)) {
      this.addStream(
        getSettingsSlideDeckTags(this.repository),
        (settingsSlideDeckTags) => {
          this.settingsSlideDeckTags.replaceModels(settingsSlideDeckTags)
        },
        {
          name: streamKey,
        }
      )
    }
    return this._settingsSlideDeckTags
  }

  /**
   * Add a new slide deck to the library
   *
   * @returns string - The ID of the newly created slide deck
   */
  addSlideDeck(): Promise<string> {
    return createSlideDeck(this.repository)
  }

  async addFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await addUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
        slideDeckTypeId: slideDeck.data.slideDeckTypeId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  async removeFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await removeUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  uploadSettingsProcessingVideo = async (file: File) => {
    return uploadSettingsProcessingVideo(this.repository, file)
  }

  saveSettingsSlideDeckTags = async (
    params: Parameters<typeof saveSettingsSlideDeckTags>[1]
  ) => {
    return await saveSettingsSlideDeckTags(this.repository, {
      ...params,
    })
  }

  hideSettingsSlideDeckTag = async (
    params: Parameters<typeof hideSettingsSlideDeckTag>[1]
  ) => {
    return await hideSettingsSlideDeckTag(this.repository, {
      ...params,
    })
  }

  triggerEmailTemplateRun = async (
    kind:
      | 'dailySessionReminder'
      | 'roomStateMessage'
      | 'sessionSoonReminder'
      | 'sessionResultsAvailable'
  ) => {
    const callFunc = httpsCallable<string, Record<string, unknown>[]>(
      this.repository.functions,
      'invokeEmailTemplateRunForSuperAdminV1'
    )
    try {
      const { data } = await callFunc(kind)

      return {
        success: true,
        message: data.length
          ? `${data.length} emails sent`
          : `No emails to send`,
        results: data,
      }
    } catch (e) {
      captureException(e)
      const msg = e instanceof FirebaseError ? e.message : e
      return {
        success: false,
        message: `Error invoking email template run:, ${msg}`,
        results: [],
      }
    }
  }

  getFeedbackAggregateForDeck(deckId: string) {
    return dependentOn(this.feedbackAggregatesLoaded, () =>
      this.feedbackAggregateByDeckId.get(deckId)
    )
  }

  getFeedbackAggregateForTypeId(typeId: string) {
    return dependentOn(this.feedbackAggregatesLoaded, () =>
      this.feedbackAggregateByTypeId.get(typeId)
    )
  }
}
